import React from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, FileText, Cookie, CreditCard } from 'lucide-react';

function LegalPage() {
  const { type } = useParams();

  const legalContent = {
    privacy: {
      title: 'Privacy Policy',
      icon: Shield,
      lastUpdated: 'August 17, 2025',
      content: [
        {
          section: 'Information We Collect',
          text: 'We collect information you provide directly to us, such as when you create an account, enroll in a course, or contact us for support. This may include your name, email address, payment information, and course progress data.'
        },
        {
          section: 'How We Use Your Information',
          text: 'We use the information we collect to provide, maintain, and improve our services, process transactions, send you technical notices and support messages, and communicate with you about products, services, and events.'
        },
        {
          section: 'Information Sharing',
          text: 'We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy or as required by law.'
        },
        {
          section: 'Data Security',
          text: 'We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.'
        },
        {
          section: 'Your Rights',
          text: 'You have the right to access, update, or delete your personal information. You can also opt out of certain communications and request data portability.'
        }
      ]
    },
    terms: {
      title: 'Terms of Service',
      icon: FileText,
      lastUpdated: 'August 17, 2025',
      content: [
        {
          section: 'Acceptance of Terms',
          text: 'By accessing and using StartWithEnes, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.'
        },
        {
          section: 'Use License',
          text: 'Permission is granted to temporarily download one copy of the materials on StartWithEnes for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title.'
        },
        {
          section: 'Course Access',
          text: 'Course access is granted for the duration specified in your purchase. Some courses may offer lifetime access, while others may have time-limited access periods.'
        },
        {
          section: 'User Conduct',
          text: 'You agree not to use the service to transmit any material that is defamatory, offensive, or otherwise objectionable, or to engage in any activity that interferes with or disrupts the service.'
        },
        {
          section: 'Intellectual Property',
          text: 'All content, features, and functionality on StartWithEnes are owned by us and are protected by international copyright, trademark, and other intellectual property laws.'
        }
      ]
    },
    cookies: {
      title: 'Cookie Policy',
      icon: Cookie,
      lastUpdated: 'August 17, 2025',
      content: [
        {
          section: 'What Are Cookies',
          text: 'Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience and understand how you use our site.'
        },
        {
          section: 'How We Use Cookies',
          text: 'We use cookies to remember your preferences, analyze site traffic, personalize content, and provide social media features. This helps us improve our website and deliver a better user experience.'
        },
        {
          section: 'Types of Cookies We Use',
          text: 'We use essential cookies for basic site functionality, analytics cookies to understand site usage, and preference cookies to remember your settings and choices.'
        },
        {
          section: 'Managing Cookies',
          text: 'You can control and manage cookies through your browser settings. However, disabling certain cookies may affect the functionality of our website.'
        },
        {
          section: 'Third-Party Cookies',
          text: 'Some cookies are placed by third-party services that appear on our pages. We do not control these cookies and they are subject to the third party\'s privacy policy.'
        }
      ]
    },
    refund: {
      title: 'Refund Policy',
      icon: CreditCard,
      lastUpdated: 'August 17, 2025',
      content: [
        {
          section: '30-Day Money-Back Guarantee',
          text: 'We offer a 30-day money-back guarantee on all course purchases. If you\'re not satisfied with your course within 30 days of purchase, you can request a full refund.'
        },
        {
          section: 'Refund Eligibility',
          text: 'To be eligible for a refund, you must submit your request within 30 days of the original purchase date. Refunds are processed within 5-7 business days.'
        },
        {
          section: 'How to Request a Refund',
          text: 'To request a refund, contact our support team with your order details and reason for the refund request. We\'ll review your request and process it accordingly.'
        },
        {
          section: 'Refund Exclusions',
          text: 'Refunds are not available for courses that have been completed more than 50% or for courses purchased more than 30 days ago. Enterprise and custom training packages may have different terms.'
        },
        {
          section: 'Processing Time',
          text: 'Once approved, refunds are typically processed within 5-7 business days. The time it takes for the refund to appear in your account depends on your payment method and financial institution.'
        }
      ]
    }
  };

  const currentContent = legalContent[type] || legalContent.privacy;

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      <div className="container mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-2xl mb-6"
          >
            <currentContent.icon className="w-10 h-10 text-blue-600" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-4xl md:text-5xl font-bold text-slate-900 mb-6"
          >
            {currentContent.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-slate-600"
          >
            Last updated: {currentContent.lastUpdated}
          </motion.p>
        </div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="prose prose-slate max-w-none">
              {currentContent.content.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                  className="mb-8 last:mb-0"
                >
                  <h3 className="text-xl font-semibold text-slate-900 mb-4">
                    {item.section}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {item.text}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Contact Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className="mt-12 pt-8 border-t border-slate-200"
            >
              <h3 className="text-lg font-semibold text-slate-900 mb-4">
                Questions About This Policy?
              </h3>
              <p className="text-slate-600 mb-4">
                If you have any questions about this policy or our practices, please contact us:
              </p>
              <div className="space-y-2 text-slate-600">
                <p><strong>Email:</strong> enes.ucar.2535@gmail.com</p>
                <p><strong>Phone:</strong> +1 (555) 123-4567</p>
                <p><strong>Address:</strong> 123 Education St, Learning City, LC 12345, United States</p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default LegalPage;
